<script setup lang="ts">
import { useClassStudents } from '@/composables/student/useClassStudents';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import type { Components } from '@/api/generated/api';
import { fetchSupportMeasureEntry, fetchSupportMeasureEntryStudents } from '@/api/resources/supportMeasureEntry.api';
import { supportMeasureEntryKeys } from '@/composables/supportMeasureEntry/supportMeasureEntry.keys';
import { useCompetenceDomain } from '@/composables/useCompetenceDomain';
import { EConfirmDialogResult, useConfirmDialog } from '@/composables/useConfirmDialog';
import { useDisplayName } from '@/composables/useDisplayName';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useStudentSelectionLabel } from '@/composables/useStudentSelectionLabel';
import { useSupportMeasureEntry } from '@/composables/supportMeasureEntry/useSupportMeasureEntry';
import { useSupportMeasureFeature } from '@/composables/supportMeasureEntry/useSupportMeasureFeature';
import { useSupportMeasureCheckout } from '@/composables/supportMeasureEntry/useSupportMeasureCheckout';
import { useSupportMeasureReportPdf } from '@/composables/supportMeasureEntry/useSupportMeasureReportPdf';
import { useSupportMeasureReportDialog } from '@/composables/supportMeasureEntry/useSupportMeasureReportDialog';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useDialog } from '@/core/dialogsystem/useDialog';
import { showAnchoredPicker } from '@/core/dialogs/anchoredPicker.utils';
import { createSupportMeasureActionMenu } from '@/core/dialogs/entryActionMenu.utils';
import TabbedDetailDialog from '@/core/dialogs/tabbed-detail-dialog/TabbedDetailDialog.vue';
import type {
  ITabbedDetailDialogMetadata,
  ITabbedDetailDialogTab,
  TTabbedDetailDialogCloseReason,
  TTabbedDetailDialogSize,
} from '@/core/dialogs/tabbed-detail-dialog/tabbedDetailDialog.types';
import ThreeDotsActionDialog from '@/core/dialogs/ThreeDotsActionDialog.vue';
import MobileSideDrawer from '@/core/dialogs/MobileSideDrawer.vue';
import MobileDetailNavigationBar from '@/core/navigation/MobileDetailNavigationBar.vue';
import CompetenceSelectionPopover from '@/core/selects/CompetenceSelectionPopover.vue';
import StudentSelectionPopover from '@/core/selects/StudentSelectionPopover.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import IconButton from '@/core/elements/IconButton.vue';
import LocalLoadingSpinner from '@/core/elements/LocalLoadingSpinner.vue';
import DialogCompletedBanner from '@/core/dialogs/base-dialog/DialogCompletedBanner.vue';
import BillingUpsellCard from '@/core/billing/BillingUpsellCard.vue';
import CompleteSupportMeasureDialog, { type ICompleteSupportMeasureResult } from './CompleteSupportMeasureDialog.vue';
import SupportMeasureDetailsTab from './SupportMeasureDetailsTab.vue';
import SupportMeasureIcon from '@/assets/icons/SupportMeasureIcon.vue';
import SupportMeasureQuickEntryComposer from './SupportMeasureQuickEntryComposer.vue';
import SupportMeasureProtocolTimeline from './SupportMeasureProtocolTimeline.vue';
import SupportMeasureStudentFilterList from './SupportMeasureStudentFilterList.vue';
import { getSupportMeasureReportAvailableContent } from './supportMeasureReportContent.utils';
import TimelineQuickEntryDock from '@/core/timeline/TimelineQuickEntryDock.vue';
import { EIcon } from 'common/enum/misc.enum';
import type { IDropdownListAction, ISelectedCompetence, TStudentLike } from '@/interfaces';
import { toCompetenceSelectionRecord } from '@/utils/competence.domain.utils';
import {
  areAllEligibleStudentsSelected,
  getEligibleStudentsForSubject,
  hasMissingEligibleStudents,
} from '@/utils/students.utils';
import { useElementSize } from '@vueuse/core';
import { arrayDiff } from 'common/utils/array.utils';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface IShowParams {
  entryId: number;
}

const props = withDefaults(
  defineProps<{
    /** No mutation is possible at all (archived class / school context) — the backend rejects every write. */
    disabled?: boolean;
    onClose?: () => Promise<void> | void;
  }>(),
  { disabled: false, onClose: undefined },
);

const { t } = useI18n();
const {
  addCompetences,
  addStudents,
  complete,
  remove,
  removeCompetence,
  removeStudentFinally,
  setCompetences,
  setStudents,
} = useSupportMeasureEntry();
const { hasSupportMeasures } = useSupportMeasureFeature();
const { showProductCheckoutDialog } = useSupportMeasureCheckout();
const { confirm, confirmWithResult } = useConfirmDialog();
const { getStudentDisplayName, getUserDisplayNameOrEmail } = useDisplayName();
const { allCompetenceDomains } = useCompetenceDomain();
const { allSchoolSubjects } = useSchoolSubject();
const { currentClass } = useCurrentClass();
const { allStudents } = useClassStudents(() => currentClass.value?.id);

const dialogRef = useTemplateRef<InstanceType<typeof TabbedDetailDialog>>('dialogRef');
const sidebarRef = useTemplateRef<HTMLElement>('sidebarRef');
const composerDockRef = useTemplateRef<HTMLElement>('composerDockRef');
const mobileStudentNavigationRef =
  useTemplateRef<InstanceType<typeof MobileDetailNavigationBar>>('mobileStudentNavigationRef');
const studentSelectionPopoverRef =
  useTemplateRef<InstanceType<typeof StudentSelectionPopover>>('studentSelectionPopoverRef');
const competenceSelectionPopoverRef = useTemplateRef<InstanceType<typeof CompetenceSelectionPopover>>(
  'competenceSelectionPopoverRef',
);
const { height: composerDockHeight } = useElementSize(composerDockRef);

const entryId = ref<number | null>(null);
const activeTab = ref('documentation');
// null = timeline shows all students; a value scopes the timeline + new protocol entries to that student
const selectedStudentId = ref<number | null>(null);
const mobileStudentSidebarOpen = ref(false);
const relationPickerAnchor = ref<HTMLElement | null>(null);

const queryKey = computed(() => supportMeasureEntryKeys.detail(entryId.value ?? 0));
const { data } = useQueryWithToast({
  queryKey,
  queryFn: () => fetchSupportMeasureEntry({ id: entryId.value! }),
  enabled: computed(() => entryId.value != null),
});
const entry = computed(() => data.value ?? null);
const isCompleted = computed(() => entry.value?.completedAt != null);
const isReadonly = computed<boolean>(() => props.disabled || isCompleted.value || !hasSupportMeasures.value);
const eligibleStudents = computed<TStudentLike[]>(() =>
  entry.value ? getEligibleStudentsForSubject(allStudents.value, entry.value.schoolSubjectId) : [],
);
const allEligibleStudentsSelected = computed<boolean>(() => {
  if (!entry.value) return false;
  return areAllEligibleStudentsSelected(entry.value.students, eligibleStudents.value);
});
const canAddStudents = computed<boolean>(() => {
  if (!entry.value || isReadonly.value) return false;
  return hasMissingEligibleStudents(entry.value.students, eligibleStudents.value);
});
const subjectDomains = computed<Components.Schemas.CompetenceDomainDto[]>(() =>
  entry.value
    ? allCompetenceDomains.value.filter((domain) => domain.schoolSubjectId === entry.value?.schoolSubjectId)
    : [],
);
const competenceRowById = computed(() => toCompetenceSelectionRecord(subjectDomains.value));
const selectedCompetences = computed<ISelectedCompetence[]>(() =>
  (entry.value?.competences ?? [])
    .map((link) => competenceRowById.value[link.competenceId])
    .filter((row): row is ISelectedCompetence => row != null),
);

// current members + former members who still have protocol/activity history (computed server-side); the
// removed ones stay filterable in the sidebar (greyed) but are not offered in the composer
const { data: filterStudentsData } = useQueryWithToast({
  queryKey: computed(() => supportMeasureEntryKeys.filterStudents(entryId.value ?? 0)),
  queryFn: () => fetchSupportMeasureEntryStudents({ id: entryId.value! }),
  enabled: computed(() => entryId.value != null),
});
const sidebarStudents = computed(() => filterStudentsData.value ?? []);
const activeSidebarStudents = computed(() => sidebarStudents.value.filter((student) => !student.isRemoved));
const { label: studentOverviewLabel } = useStudentSelectionLabel(activeSidebarStudents, allStudents);
const filterableStudentIds = computed(() => new Set(sidebarStudents.value.map((student) => student.id)));
const showSidebar = computed<boolean>(() => sidebarStudents.value.length > 1 || canAddStudents.value);
// a removed student can be filtered but not documented — hide the composer while one is selected
const isRemovedStudentSelected = computed<boolean>(() =>
  sidebarStudents.value.some((student) => student.id === selectedStudentId.value && student.isRemoved),
);
const showProtocolComposer = computed<boolean>(() => !isReadonly.value && !isRemovedStudentSelected.value);

const tabs = computed<ITabbedDetailDialogTab[]>(() => [
  { label: t('supportMeasureEntry.documentation'), value: 'documentation' },
  { label: t('general.details'), value: 'details' },
]);
const dialogSize = computed<TTabbedDetailDialogSize>(() => (activeTab.value === 'documentation' ? 'wide' : 'compact'));

// footer shows "Erstellt/Bearbeitet by {name}" — only on the details tab
const metadata = computed<ITabbedDetailDialogMetadata | null>(() =>
  entry.value && activeTab.value === 'details'
    ? {
        creator: entry.value.creator,
        createdAt: entry.value.createdAt,
        updater: entry.value.lastEditedBy ?? entry.value.creator,
        updatedAt: entry.value.updatedAt,
      }
    : null,
);

// fall back to "all students" only when the selected student is no longer filterable (not a member AND
// has no history) — a removed-with-history student stays selectable in the sidebar
watch(filterableStudentIds, (ids) => {
  if (selectedStudentId.value != null && !ids.has(selectedStudentId.value)) selectedStudentId.value = null;
});

// Mobile: the sidebar becomes a drawer + a prev/next student switcher (the desktop sidebar is hidden).
// Inactive students remain selectable in the drawer, but the arrows cycle only through All + active students.
const studentCycle = computed<(number | null)[]>(() => [
  null,
  ...activeSidebarStudents.value.map((student) => student.id),
]);
const mobileFilterLabel = computed<string>(() => {
  const selected = sidebarStudents.value.find((student) => student.id === selectedStudentId.value);
  return selected ? getStudentDisplayName(selected) : studentOverviewLabel.value;
});
const selectRelativeStudent = (offset: -1 | 1): void => {
  const cycle = studentCycle.value;
  if (cycle.length < 2) return;
  // From an inactive drawer selection, move directly to the first/last active student depending on direction.
  const currentIndex = Math.max(0, cycle.indexOf(selectedStudentId.value));
  selectedStudentId.value = cycle[(currentIndex + offset + cycle.length) % cycle.length];
};

const openMobileStudentSidebar = (): void => void (mobileStudentSidebarOpen.value = true);
const closeMobileStudentSidebar = (restoreFocus = true): void => {
  mobileStudentSidebarOpen.value = false;
  if (restoreFocus) void nextTick(() => mobileStudentNavigationRef.value?.focusOpenButton());
};
const handleMobileStudentSelect = (studentId: number | null): void => {
  selectedStudentId.value = studentId;
  closeMobileStudentSidebar();
};

// the switcher/drawer only belong to the documentation tab
watch(activeTab, () => (mobileStudentSidebarOpen.value = false));

const persistStudents = (nextIds: number[]): void => {
  if (!entry.value) return;
  // A support measure must keep at least one student; the backend rejects an empty selection.
  if (nextIds.length === 0) return;
  const currentIds = entry.value.students.map((student) => student.id);
  const { toAdd, toRemove } = arrayDiff(currentIds, nextIds);
  if (!toAdd.length && !toRemove.length) return;
  void setStudents(entry.value.id, nextIds, { added: toAdd.length > 0, removed: toRemove.length > 0 });
};

const handleStudentsUpdate = (students: TStudentLike[]): void => {
  persistStudents(students.map((student) => student.id));
};

const handleSelectAllEligibleStudents = (): void => {
  persistStudents(eligibleStudents.value.map((student) => student.id));
};

const handleCompetencesUpdate = (competenceIds: number[]): void => {
  if (!entry.value) return;
  const currentIds = entry.value.competences.map((link) => link.competenceId);
  const { toAdd, toRemove } = arrayDiff(currentIds, competenceIds);
  if (!toAdd.length && !toRemove.length) return;
  void setCompetences(entry.value.id, competenceIds, { added: toAdd.length > 0, removed: toRemove.length > 0 });
};

const handleAddCompetence = (competenceId: number): void => {
  if (entry.value) void addCompetences(entry.value.id, [competenceId]);
};

const handleRemoveCompetence = (competenceId: number): void => {
  if (entry.value) void removeCompetence(entry.value.id, competenceId);
};

const rememberActionMenuAnchor = (anchor: HTMLElement): void => {
  relationPickerAnchor.value = anchor;
};

const openStudentSelection = async (anchor?: HTMLElement): Promise<void> => {
  if (isReadonly.value) return;
  await showAnchoredPicker({
    anchor,
    setAnchor: (value) => {
      relationPickerAnchor.value = value;
    },
    hideOther: () => competenceSelectionPopoverRef.value?.hide(),
    beforeShow: () => {
      if (mobileStudentSidebarOpen.value) closeMobileStudentSidebar(false);
    },
    show: () => studentSelectionPopoverRef.value?.show(),
  });
};

const openCompetenceSelection = async (): Promise<void> => {
  if (isReadonly.value) return;
  await showAnchoredPicker({
    hideOther: () => studentSelectionPopoverRef.value?.hide(),
    show: () => competenceSelectionPopoverRef.value?.show(),
  });
};

const hideRelationPickers = (): void => {
  studentSelectionPopoverRef.value?.hide();
  competenceSelectionPopoverRef.value?.hide();
  relationPickerAnchor.value = null;
};

const handleDelete = async (): Promise<void> => {
  if (!entry.value) return;
  const confirmed = await confirm({
    title: t('supportMeasureEntry.deleteEntryTitle'),
    description: t('supportMeasureEntry.deleteEntryDescription'),
    confirmButtonLabel: t('general.delete'),
    declineButtonLabel: t('general.cancel'),
    theme: 'danger',
  });
  if (!confirmed) return;
  await remove(entry.value.id);
  void dialogRef.value?.hide();
};

const handleReaddStudent = (studentId: number): void => {
  if (!entry.value) return;
  selectedStudentId.value = studentId;
  void addStudents(entry.value.id, [studentId]);
};

const handleRemoveStudentFinally = async (studentId: number): Promise<void> => {
  if (!entry.value) return;
  const id = entry.value.id;
  const student = sidebarStudents.value.find((candidate) => candidate.id === studentId);
  const name = student ? getStudentDisplayName(student) : '';

  if (sidebarStudents.value.length <= 1) {
    const result = await confirmWithResult({
      title: t('supportMeasureEntry.removeLastStudentConfirm.title'),
      description: t('supportMeasureEntry.removeLastStudentConfirm.description', { name }),
      confirmButtonLabel: t('supportMeasureEntry.removeLastStudentConfirm.deleteEntry'),
      declineButtonLabel: t('supportMeasureEntry.removeLastStudentConfirm.keepEntry'),
      theme: 'danger',
      size: 'xl',
    });
    if (result === EConfirmDialogResult.Confirm) {
      await remove(id);
      void dialogRef.value?.hide();
    } else if (result === EConfirmDialogResult.Decline) {
      await removeStudentFinally(id, studentId);
    }
    return;
  }

  const confirmed = await confirm({
    title: t('supportMeasureEntry.removeStudentConfirm.title'),
    description: t('supportMeasureEntry.removeStudentConfirm.description', { name }),
    confirmButtonLabel: t('supportMeasureEntry.removeStudentConfirm.confirm'),
    declineButtonLabel: t('general.cancel'),
    theme: 'danger',
    size: 'lg',
  });
  if (!confirmed) return;
  await removeStudentFinally(id, studentId);
};

const onCompleteConfirmed = (result: ICompleteSupportMeasureResult): void => {
  if (entry.value) void complete(entry.value.id, result);
};
const completeDialog = useDialog({
  component: CompleteSupportMeasureDialog,
  props: { onComplete: onCompleteConfirmed },
});

// Without the support measures feature, completing and creating reports lead to the checkout instead.
const runWithSupportMeasures = (action: (supportMeasure: Components.Schemas.SupportMeasureEntryDto) => void): void => {
  if (!entry.value) return;
  if (!hasSupportMeasures.value) {
    showProductCheckoutDialog();
    return;
  }
  action(entry.value);
};

const handleComplete = (): void =>
  runWithSupportMeasures((supportMeasure) => completeDialog.show({ studentCount: supportMeasure.students.length }));

// The overview creates a report per current member; a selected student (also a former one) gets only their own.
const reportStudentIds = computed<number[] | undefined>(() =>
  selectedStudentId.value == null ? undefined : [selectedStudentId.value],
);
const reportStudentCount = computed<number>(() => reportStudentIds.value?.length ?? entry.value?.students.length ?? 0);
const createReportLabel = computed<string>(() =>
  reportStudentCount.value > 1
    ? t('supportMeasureEntry.report.createAll')
    : t('supportMeasureEntry.report.createReport'),
);
const { downloadReports, loading: reportPdfLoading } = useSupportMeasureReportPdf();
const { showReportDialog } = useSupportMeasureReportDialog((options) => {
  if (!entry.value) return;
  void downloadReports({
    entryId: entry.value.id,
    studentIds: reportStudentIds.value,
    studentCount: reportStudentCount.value,
    options,
  });
});

const handleCreateReports = (): void =>
  runWithSupportMeasures((supportMeasure) =>
    showReportDialog(getSupportMeasureReportAvailableContent(supportMeasure, reportStudentIds.value)),
  );

const getDropdownActions = (mobile: boolean): IDropdownListAction[] =>
  createSupportMeasureActionMenu({
    studentsLabel: t('students.editStudents'),
    competencesLabel: t('competence.editPlural'),
    editStudents: () => openStudentSelection(),
    editCompetences: openCompetenceSelection,
    completeLabel: t('supportMeasureEntry.complete'),
    complete: handleComplete,
    deleteLabel: t('general.delete'),
    deleteEntry: handleDelete,
    mobile,
    createReportLabel: createReportLabel.value,
    createReport: handleCreateReports,
    readonly: isReadonly.value,
    showCreateReport: !isCompleted.value,
  });

const dropdownActions = computed(() => getDropdownActions(false));
const mobileDropdownActions = computed(() => getDropdownActions(true));

const show = async (params: IShowParams): Promise<void> => {
  entryId.value = params.entryId;
  activeTab.value = 'documentation';
  selectedStudentId.value = null;
  mobileStudentSidebarOpen.value = false;
  relationPickerAnchor.value = null;
  await dialogRef.value?.show();
};
const hide = (): void => void dialogRef.value?.hide();
const beforeClose = (reason: TTabbedDetailDialogCloseReason): boolean => {
  if (reason === 'escape' && mobileStudentSidebarOpen.value) {
    closeMobileStudentSidebar();
    return false;
  }
  return true;
};
const handleClose = async (): Promise<void> => {
  hideRelationPickers();
  mobileStudentSidebarOpen.value = false;
  entryId.value = null;
  await props.onClose?.();
};

defineExpose({ show, hide });
</script>

<template>
  <TabbedDetailDialog
    ref="dialogRef"
    v-model:active-tab="activeTab"
    :before-close="beforeClose"
    :tabs="tabs"
    :title="$t('supportMeasureEntry.dialogTitle')"
    :size="dialogSize"
    height="tall"
    :metadata="metadata"
    :modal-overlay-active="mobileStudentSidebarOpen"
    :close-disabled="reportPdfLoading"
    @close="handleClose"
  >
    <template #title-badge>
      <SchoolSubjectBadge v-if="entry" :school-subject="entry.schoolSubject" />
    </template>

    <template #banner>
      <DialogCompletedBanner
        v-if="entry && isCompleted"
        :completed-at="entry.completedAt"
        :completed-by-name="entry.completedBy ? getUserDisplayNameOrEmail(entry.completedBy) : null"
      />
    </template>

    <template #mobile-toolbar>
      <MobileDetailNavigationBar
        v-if="entry && activeTab === 'documentation' && showSidebar"
        ref="mobileStudentNavigationRef"
        :label="mobileFilterLabel"
        :next-label="$t('supportMeasureEntry.nextStudent')"
        :open-label="$t('supportMeasureEntry.openStudentList')"
        :previous-label="$t('supportMeasureEntry.previousStudent')"
        @open="openMobileStudentSidebar"
        @previous="selectRelativeStudent(-1)"
        @next="selectRelativeStudent(1)"
      />
    </template>

    <template #overlay>
      <MobileSideDrawer
        v-if="entry && showSidebar"
        :open="mobileStudentSidebarOpen"
        :title="studentOverviewLabel"
        @close="closeMobileStudentSidebar()"
      >
        <SupportMeasureStudentFilterList
          :overview-label="studentOverviewLabel"
          :selected-student-id="selectedStudentId"
          :show-add-students="canAddStudents"
          :students="sidebarStudents"
          :readonly="isReadonly"
          @update:selected-student-id="handleMobileStudentSelect"
          @readd="handleReaddStudent"
          @remove-finally="handleRemoveStudentFinally"
          @request-add="openStudentSelection"
        />
      </MobileSideDrawer>

      <StudentSelectionPopover
        v-if="entry"
        ref="studentSelectionPopoverRef"
        :anchor-element="relationPickerAnchor"
        :all-eligible-students-selected="allEligibleStudentsSelected"
        commit-on-close
        :eligible-students="eligibleStudents"
        mobile-drawer
        :selected-students="entry.students"
        @select:all-eligible-students="handleSelectAllEligibleStudents"
        @update:students="handleStudentsUpdate"
      />

      <CompetenceSelectionPopover
        v-if="entry"
        ref="competenceSelectionPopoverRef"
        :anchor-element="relationPickerAnchor"
        :all-school-subjects="allSchoolSubjects"
        :available-competence-domains="subjectDomains"
        commit-on-close
        hide-school-subjects
        mobile-drawer
        :selected-competences="selectedCompetences.map(({ competence }) => competence)"
        @add:competence="handleAddCompetence"
        @remove:competence="handleRemoveCompetence"
        @update:competences="handleCompetencesUpdate"
      />
    </template>

    <template #header-actions>
      <div v-if="entry" class="flex items-center gap-sm">
        <!-- archiving completes every measure, so archived classes create reports from here too -->
        <CustomButton
          v-if="isCompleted"
          class="flex items-center gap-sm px-[20px]"
          theme="surface"
          type="button"
          @click="handleCreateReports"
        >
          <AnyIcon :icon-name="EIcon.DOWNLOAD" class="size-icon-md" />
          <span>{{ createReportLabel }}</span>
        </CustomButton>
        <template v-if="!disabled">
          <CustomButton
            v-if="!isReadonly"
            class="flex items-center gap-sm px-[20px]"
            theme="surface"
            type="button"
            @click="handleComplete"
          >
            <AnyIcon :icon-name="EIcon.CHECKMARK" class="size-icon-md" />
            <span>{{ $t('supportMeasureEntry.complete') }}</span>
          </CustomButton>
          <ThreeDotsActionDialog
            :actions="dropdownActions"
            :min-width="260"
            size="md"
            @show="rememberActionMenuAnchor"
          />
        </template>
      </div>
    </template>

    <template #mobile-header-actions>
      <IconButton
        v-if="entry && isCompleted"
        type="button"
        size="button"
        theme="text"
        :icon-name="EIcon.DOWNLOAD"
        :aria-label="createReportLabel"
        @click="handleCreateReports"
      />
      <ThreeDotsActionDialog
        v-if="entry && !disabled"
        :actions="mobileDropdownActions"
        :min-width="260"
        size="md"
        @show="rememberActionMenuAnchor"
      />
    </template>

    <template #default>
      <LocalLoadingSpinner :show="reportPdfLoading" />
      <template v-if="entry">
        <div
          v-if="activeTab === 'documentation'"
          class="grid h-full min-h-0 gap-content overflow-hidden p-content max-md:flex max-md:flex-col max-md:gap-lg max-md:p-lg"
          :class="showSidebar ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]' : 'md:grid-cols-[minmax(0,1fr)]'"
        >
          <aside
            v-if="showSidebar"
            ref="sidebarRef"
            class="min-h-0 min-w-0 overflow-y-auto overscroll-contain max-md:hidden"
          >
            <SupportMeasureStudentFilterList
              v-model:selected-student-id="selectedStudentId"
              :overview-label="studentOverviewLabel"
              :show-add-students="canAddStudents"
              :students="sidebarStudents"
              :readonly="isReadonly"
              @readd="handleReaddStudent"
              @remove-finally="handleRemoveStudentFinally"
              @request-add="openStudentSelection"
            />
          </aside>

          <!-- The banner and composer stay outside the scrollport so the composer's shadow remains unclipped.
               The timeline reserves the composer's live height, then scrolls underneath its overlay. -->
          <section class="flex min-h-0 min-w-0 flex-col gap-lg max-md:flex-1">
            <BillingUpsellCard
              v-if="!isCompleted && !hasSupportMeasures"
              :aria-label="$t('supportMeasureEntry.upsellAriaLabel')"
              :description="$t('supportMeasureEntry.upsellDescription')"
              :title="$t('supportMeasureEntry.upsellTitle')"
              @action="showProductCheckoutDialog()"
            >
              <template #icon>
                <SupportMeasureIcon class="size-icon-button text-tint" />
              </template>
            </BillingUpsellCard>

            <div class="relative min-h-0 flex-1">
              <div v-if="showProtocolComposer" ref="composerDockRef" class="absolute inset-x-0 top-0 z-20">
                <TimelineQuickEntryDock class="md:pb-lg" mobile-placement="viewport-dock" :vertical-padding="false">
                  <SupportMeasureQuickEntryComposer
                    :support-measure-entry-id="entry.id"
                    :school-subject-id="entry.schoolSubjectId"
                    :school-class-id="entry.schoolClassId"
                    :students="entry.students"
                    :selected-student-id="selectedStudentId"
                    :ignore-click-outside-el="sidebarRef"
                    :connected-competence-ids="entry.competences.map((link) => link.competenceId)"
                  />
                </TimelineQuickEntryDock>
              </div>

              <div
                class="h-full min-h-0 overflow-y-auto overscroll-contain pr-sm"
                :class="showProtocolComposer && 'max-md:pb-quick-entry-mobile-dock-safe'"
              >
                <div class="flex min-h-full flex-col">
                  <!-- Reserve the overlay's measured height so the timeline starts below it but can scroll underneath. -->
                  <div
                    v-if="showProtocolComposer"
                    aria-hidden="true"
                    class="hidden shrink-0 md:block"
                    :style="{ height: `${composerDockHeight}px` }"
                  />

                  <SupportMeasureProtocolTimeline
                    class="flex-1"
                    :entry-id="entry.id"
                    :school-subject-id="entry.schoolSubjectId"
                    :school-class-id="entry.schoolClassId"
                    :protocol="entry.protocol"
                    :activities="entry.activities"
                    :students="entry.students"
                    :selected-student-id="selectedStudentId"
                    :subject-color="entry.schoolSubject.color"
                    :readonly="isReadonly"
                    :completed-at="entry.completedAt"
                    :completion-note="entry.completionNote"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        <SupportMeasureDetailsTab
          v-else
          :all-eligible-students-selected="allEligibleStudentsSelected"
          :all-school-subjects="allSchoolSubjects"
          :eligible-students="eligibleStudents"
          :entry="entry"
          :readonly="isReadonly"
          :selected-competences="selectedCompetences"
          :subject-domains="subjectDomains"
          @add:competence="handleAddCompetence"
          @remove:competence="handleRemoveCompetence"
          @select:all-students="handleSelectAllEligibleStudents"
          @update:competences="handleCompetencesUpdate"
          @update:students="handleStudentsUpdate"
        />
      </template>
    </template>
  </TabbedDetailDialog>
</template>
